import { gql } from "@apollo/client";

// Member ID validation
export const CHECK_MEMBER = gql`
  query CheckMemberId($memberId: String!, $link: String!) {
    checkMemberId(memberId: $memberId, link: $link) {
      isValid
      message
      discount
    }
  }
`;

// Promo code validation
export const VALIDATE_PROMO = gql`
  query ValidatePromo($code: String!, $link: String!, $email: String) {
    validatePromo(code: $code, link: $link, email: $email) {
      status
      discountValue
      discountPercent
      message
    }
  }
`;

// Social Media Authentication Queries
export const GET_SOCIAL_AUTH_URL = gql`
  query GetSocialAuthUrl($provider: SocialProvider!, $link: String!) {
    getSocialAuthUrl(provider: $provider, link: $link) {
      provider
      authUrl
      state
    }
  }
`;

export const HANDLE_SOCIAL_CALLBACK = gql`
  query HandleSocialCallback(
    $provider: SocialProvider!
    $code: String!
    $state: String!
  ) {
    handleSocialCallback(provider: $provider, code: $code, state: $state) {
      success
      socialData
      mappedData
      missingFields
      link
      provider
    }
  }
`;

// Invitation Registration
export const INVITATION_REGISTER = gql`
  mutation InvitationRegister(
    $uid: String!
    $userData: String!
    $action: String!
  ) {
    invitationRegister(uid: $uid, userData: $userData, action: $action) {
      success
      message
      nanoId
      regLink
      action
    }
  }
`;

// Social Media Registration Mutation
export const SOCIAL_REGISTER = gql`
  mutation SocialRegister(
    $link: String!
    $provider: SocialProvider!
    $socialData: String!
    $additionalData: String!
  ) {
    socialRegister(
      link: $link
      provider: $provider
      socialData: $socialData
      additionalData: $additionalData
    )
  }
`;

// Get registration link details
export const GET_LINK = gql`
  query GetLink($link: String!) {
    getLink(link: $link) {
      link
      eventId
      role
      registrationLink
      active
      event {
        id
        name
        description
        startDate
        endDate
        location
        logo
        createdBy
        status
      }
    }
  }
`;

// Get event redirect link
export const GET_EVENT_REDIRECT_LINK = gql`
  query GetEventRedirectLink($eventId: ID!, $role: String!) {
    getEventRedirectLink(eventId: $eventId, role: $role) {
      link
      utmLink
      eventName
      eventDescription
      eventImage
    }
  }
`;

// Get form fields for registration
export const GET_FORM = gql`
  query GetForm($eventId: ID!, $role: String!) {
    getForm(eventId: $eventId, role: $role) {
      eventId
      role
      fields
      features
      action
      submitButtonText
      keywords
    }
  }
`;

// Get payment details
export const GET_PAYMENT = gql`
  query GetPayment($eventId: ID!, $role: String!) {
    getPayment(eventId: $eventId, role: $role) {
      eventId
      role
      paymentMethod
      currency
      amount
      clientId
      secretKey
      publicKey
      integrationId
      frameId
      tax
      free
      required
    }
  }
`;

// Check if email is registered
export const CHECK_EMAIL = gql`
  query CheckEmail($eventId: ID!, $role: String!, $email: String!) {
    checkEmail(eventId: $eventId, role: $role, email: $email) {
      exists
      message
    }
  }
`;

// Check if mobile is registered
export const CHECK_MOBILE = gql`
  query CheckMobile($mobile: String!, $link: String!) {
    checkMobile(mobile: $mobile, link: $link)
  }
`;

// Get user registration data
export const GET_REGISTRATION = gql`
  query GetRegistration($eventId: ID!, $role: String!, $id: String!) {
    getRegistration(eventId: $eventId, role: $role, id: $id) {
      id
      eventId
      role
      data
    }
  }
`;

// Decrypt payment data
export const DECRYPT_PMT = gql`
  query DecryptPmt($encryptedPmt: String!) {
    decryptPmt(encryptedPmt: $encryptedPmt)
  }
`;

// Submit registration
export const SUBMIT_REGISTRATION = gql`
  mutation SubmitRegistration(
    $eventId: ID!
    $role: String!
    $data: JSON!
    $link: String!
  ) {
    submitRegistration(
      eventId: $eventId
      role: $role
      data: $data
      link: $link
    ) {
      id
      message
    }
  }
`;

// Submit payment
export const SUBMIT_PAYMENT = gql`
  mutation SubmitPayment(
    $eventId: ID!
    $role: String!
    $id: String!
    $paymentStatus: String!
    $paymentId: String!
    $amount: Float!
    $currency: String!
  ) {
    submitPayment(
      eventId: $eventId
      role: $role
      id: $id
      paymentStatus: $paymentStatus
      paymentId: $paymentId
      amount: $amount
      currency: $currency
    ) {
      success
      message
    }
  }
`;

// Search registrations
export const SEARCH_REGISTRATIONS = gql`
  query SearchRegistrations(
    $eventId: ID!
    $role: String!
    $searchTerm: String!
  ) {
    searchRegistrations(
      eventId: $eventId
      role: $role
      searchTerm: $searchTerm
    ) {
      id
      name
      email
    }
  }
`;

// Get badge
export const GET_BADGE = gql`
  query GetBadge($eventId: ID!, $role: String!, $id: String!) {
    getBadge(eventId: $eventId, role: $role, id: $id) {
      url
      name
      eventName
    }
  }
`;

// Get certificate
export const GET_CERTIFICATE = gql`
  query GetCertificate($eventId: ID!, $role: String!, $id: String!) {
    getCertificate(eventId: $eventId, role: $role, id: $id) {
      url
      name
      eventName
    }
  }
`;

// Verify OTP
export const VERIFY_OTP = gql`
  mutation VerifyOTP($mobile: String, $email: String, $otp: String!) {
    verifyOTP(mobile: $mobile, email: $email, otp: $otp) {
      success
      message
    }
  }
`;

// Send OTP
export const SEND_OTP = gql`
  mutation SendOTP(
    $mobile: String
    $email: String
    $eventId: String
    $role: String
  ) {
    sendOTP(mobile: $mobile, email: $email, eventId: $eventId, role: $role) {
      success
      message
    }
  }
`;

/**
 * Query to get social share settings for an event
 */
export const GET_SOCIAL_SHARE_SETTINGS = gql`
  query GetSocialShareSettings($eventId: ID!) {
    socialShareSettings(eventId: $eventId) {
      id
      eventId
      enabled
      shareTitle
      shareDescription
      bannerImage
      squareImage
      defaultMessage
      includeLink
      platforms {
        name
        enabled
        displayOrder
        customMessage
        customSubject
      }
    }
  }
`;

/**
 * Query to get basic event details for sharing
 */
export const GET_EVENT_SHARE_DETAILS = gql`
  query GetEventShareDetails($eventId: ID!) {
    event(id: $eventId) {
      id
      name
      description
      shortDescription
      logoUrl
      bannerImage
      startDate
      endDate
      venue
      address
    }
  }
`;

/**
 * Mutation to toggle social sharing for an event
 */
export const TOGGLE_SOCIAL_SHARING = gql`
  mutation ToggleSocialSharing($eventId: ID!, $enabled: Boolean!) {
    toggleSocialSharing(eventId: $eventId, enabled: $enabled) {
      id
      eventId
      enabled
    }
  }
`;

/**
 * Mutation to update social share settings
 */
export const UPDATE_SOCIAL_SHARE_SETTINGS = gql`
  mutation UpdateSocialShareSettings($input: SocialShareSettingsInput!) {
    updateSocialShareSettings(input: $input) {
      id
      eventId
      enabled
      shareTitle
      shareDescription
      bannerImage
      squareImage
      defaultMessage
      includeLink
      platforms {
        name
        enabled
        displayOrder
        customMessage
        customSubject
      }
    }
  }
`;

/**
 * Mutation to update a platform message
 */
export const UPDATE_PLATFORM_MESSAGE = gql`
  mutation UpdatePlatformMessage(
    $eventId: ID!
    $platformName: String!
    $customMessage: String
    $customSubject: String
  ) {
    updatePlatformMessage(
      eventId: $eventId
      platformName: $platformName
      customMessage: $customMessage
      customSubject: $customSubject
    ) {
      id
      platforms {
        name
        enabled
        customMessage
        customSubject
      }
    }
  }
`;
